import React, { useState } from "react";
import { Line, Pie } from "react-chartjs-2";
import Modal from "../../components/shared/Modal";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import SubPageLayout from "../../components/shared/SubPageLayout";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
);

// Technical Analysis Chart Component
const TechnicalAnalysisChart = ({
  chartOptions,
  technicalIndicators,
  selectedIndicators,
  setSelectedIndicators,
  chartTimeframe,
  setChartTimeframe
}) => {
  const chartData = {
    labels: Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (30 - i));
      return date;
    }),
    datasets: [
      {
        label: 'Price',
        data: Array.from({ length: 30 }, () => Math.random() * 100 + 150),
        borderColor: 'rgb(2, 152, 49)',
        backgroundColor: 'rgba(2, 152, 49, 0.1)',
        yAxisID: 'y',
        fill: false,
        tension: 0.4,
      },
      {
        label: 'Volume',
        data: Array.from({ length: 30 }, () => Math.random() * 1000000),
        borderColor: 'rgb(32, 46, 198)',
        backgroundColor: 'rgba(32, 46, 198, 0.1)',
        yAxisID: 'y1',
        type: 'bar',
      },
    ],
  };

  const technicalChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Price',
          color: 'var(--text-secondary)',
        },
        grid: {
          color: 'var(--border-color)',
          drawBorder: false,
        },
        ticks: {
          color: 'var(--text-secondary)',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Volume',
          color: 'var(--text-secondary)',
        },
        grid: {
          display: false,
        },
        ticks: {
          color: 'var(--text-secondary)',
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="space-x-2">
          {technicalIndicators.map((indicator) => (
            <button
              key={indicator.id}
              onClick={() => {
                if (selectedIndicators.includes(indicator.id)) {
                  setSelectedIndicators(selectedIndicators.filter(id => id !== indicator.id));
                } else {
                  setSelectedIndicators([...selectedIndicators, indicator.id]);
                }
              }}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                selectedIndicators.includes(indicator.id)
                  ? 'bg-primary dark:bg-primary-dark text-white'
                  : 'bg-surface-raised dark:bg-surface-raised-dark text-secondary dark:text-secondary-dark hover:bg-surface-raised/80 dark:hover:bg-surface-raised-dark/80'
              }`}
            >
              {indicator.id}
            </button>
          ))}
        </div>
        <div className="space-x-2">
          {['1D', '5D', '1M', '3M', '1Y'].map((timeframe) => (
            <button
              key={timeframe}
              onClick={() => setChartTimeframe(timeframe)}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                chartTimeframe === timeframe
                  ? 'bg-primary dark:bg-primary-dark text-white'
                  : 'bg-surface-raised dark:bg-surface-raised-dark text-secondary dark:text-secondary-dark hover:bg-surface-raised/80 dark:hover:bg-surface-raised-dark/80'
              }`}
            >
              {timeframe}
            </button>
          ))}
        </div>
      </div>
      <Line data={chartData} options={technicalChartOptions} />
    </div>
  );
};

const MarketResearch = () => {
  const [activeTab, setActiveTab] = useState("models");
  const [selectedModel, setSelectedModel] = useState(null);
  const [showModelModal, setShowModelModal] = useState(false);
  const [showTechnicalModal, setShowTechnicalModal] = useState(false);
  const [showHoldingsModal, setShowHoldingsModal] = useState(false);
  const [showModelCreator, setShowModelCreator] = useState(false);
  const [chartTimeframe, setChartTimeframe] = useState("1Y");
  const [selectedIndicators, setSelectedIndicators] = useState(["SMA"]);
  const [showTechnicalAnalysis, setShowTechnicalAnalysis] = useState(false);
  const [showAllHoldings, setShowAllHoldings] = useState(false);
  const [performanceCache, setPerformanceCache] = useState(null);

  // Base classes for consistent styling
  const inputBaseClasses = `
    w-full p-2 
    border border-border dark:border-border-dark rounded-lg 
    bg-white dark:bg-gray-800
    text-primary dark:text-primary-dark 
    placeholder-secondary dark:placeholder-secondary-dark
    focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-dark/20
    focus:border-primary dark:focus:border-primary-dark
    hover:border-primary/50 dark:hover:border-primary-dark/50
    transition-all duration-200
  `;

  const selectBaseClasses = `
    ${inputBaseClasses}
    appearance-none 
    bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"%3E%3Cpath stroke="%236B7280" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 8l4 4 4-4"/%3E%3C/svg%3E')] 
    bg-no-repeat bg-right bg-[length:1.5em_1.5em]
    pr-10
  `;

  const buttonBaseClasses = `
    px-4 py-2 rounded-lg
    focus:outline-none focus:ring-2
    transition-all duration-200
    disabled:opacity-50
  `;

  const primaryButtonClasses = `
    ${buttonBaseClasses}
    bg-primary dark:bg-primary-dark text-white
    hover:bg-primary/90 dark:hover:bg-primary-dark/90
    focus:ring-primary/20 dark:focus:ring-primary-dark/20
  `;

  const secondaryButtonClasses = `
    ${buttonBaseClasses}
    bg-surface-raised dark:bg-surface-raised-dark
    text-secondary dark:text-secondary-dark
    hover:bg-surface-raised/80 dark:hover:bg-surface-raised-dark/80
    focus:ring-secondary/20 dark:focus:ring-secondary-dark/20
  `;

  // Chart options for consistent styling
  const chartOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          color: '#FFFFFF',  // Bright white for dark mode
          font: {
            size: 12,
            weight: '600'  // Made slightly bolder
          },
          padding: 16
        }
      },
      tooltip: {
        backgroundColor: '#1a1a1a', // Dark but not pure black
        titleColor: '#FFFFFF', // Bright white
        bodyColor: '#FFFFFF', // Bright white
        borderColor: '#333333',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            // Convert to percentage relative to starting value (100)
            const percentChange = ((context.parsed.y - 100) / 100) * 100;
            return `${context.dataset.label}: ${percentChange.toFixed(2)}%`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false,
          display: true
        },
        ticks: {
          color: '#FFFFFF', // Bright white
          font: {
            size: 11
          }
        },
        border: {
          display: false
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false,
          display: true
        },
        ticks: {
          color: '#FFFFFF', // Bright white
          font: {
            size: 11
          },
          callback: function(value) {
            // Convert to percentage relative to starting value (100)
            const percentChange = ((value - 100) / 100) * 100;
            return percentChange.toFixed(1) + '%';
          }
        },
        border: {
          display: false
        }
      }
    }
  };

  // Technical Indicators
  const technicalIndicators = [
    { id: "SMA", label: "Simple Moving Average" },
    { id: "EMA", label: "Exponential Moving Average" },
    { id: "RSI", label: "Relative Strength Index" },
    { id: "MACD", label: "MACD" },
    { id: "BB", label: "Bollinger Bands" },
  ];

  // Mock data for models
  const models = [
    {
      id: 1,
      name: "Bond Alternative",
      type: "Fixed Income",
      riskScore: 1,
      status: "Active",
      lastUpdated: "2024-01-14",
      performance: {
        ytd: "+2.5%",
        oneYear: "+8.2%",
        threeYear: "+24.6%",
        volatility: "3.2%",
        beta: 0.15,
        alpha: 2.1,
      },
    },
    {
      id: 7,
      name: "RN 15",
      type: "Equity",
      riskScore: 15,
      status: "Active",
      lastUpdated: "2024-01-13",
      performance: {
        ytd: "+1.8%",
        oneYear: "+12.4%",
        threeYear: "+32.1%",
        volatility: "8.5%",
        beta: 0.45,
        alpha: 3.2,
      },
    },
    {
      id: 8,
      name: "RN 30",
      type: "Equity",
      riskScore: 30,
      status: "Active",
      lastUpdated: "2024-01-14",
      performance: {
        ytd: "+2.9%",
        oneYear: "+15.6%",
        threeYear: "+42.3%",
        volatility: "12.4%",
        beta: 0.75,
        alpha: 4.1,
      },
    },
    {
      id: 9,
      name: "RN 45",
      type: "Equity",
      riskScore: 45,
      status: "Active",
      lastUpdated: "2024-01-14",
      performance: {
        ytd: "+3.2%",
        oneYear: "+18.7%",
        threeYear: "+48.5%",
        volatility: "15.8%",
        beta: 0.95,
        alpha: 4.8,
      },
    },
    {
      id: 10,
      name: "RN 60",
      type: "Equity",
      riskScore: 60,
      status: "Active",
      lastUpdated: "2024-01-14",
      performance: {
        ytd: "+3.8%",
        oneYear: "+21.4%",
        threeYear: "+55.2%",
        volatility: "18.2%",
        beta: 1.15,
        alpha: 5.2,
      },
    },
    {
      id: 11,
      name: "Equity",
      type: "Core Equity",
      riskScore: 50,
      status: "Active",
      lastUpdated: "2024-01-14",
      performance: {
        ytd: "+3.5%",
        oneYear: "+19.8%",
        threeYear: "+51.6%",
        volatility: "16.5%",
        beta: 1.0,
        alpha: 4.5,
      },
    },
    {
      id: 12,
      name: "NQ Equity",
      type: "Growth Equity",
      riskScore: 65,
      status: "Active",
      lastUpdated: "2024-01-14",
      performance: {
        ytd: "+4.2%",
        oneYear: "+24.6%",
        threeYear: "+62.8%",
        volatility: "20.4%",
        beta: 1.25,
        alpha: 6.1,
      },
    },
    {
      id: 13,
      name: "Sector Rotating",
      type: "Tactical Equity",
      riskScore: 55,
      status: "Active",
      lastUpdated: "2024-01-14",
      performance: {
        ytd: "+3.6%",
        oneYear: "+20.2%",
        threeYear: "+53.4%",
        volatility: "17.2%",
        beta: 1.05,
        alpha: 5.4,
      },
    },
    {
      id: 14,
      name: "Artificial Intelligence & Innovation",
      type: "Thematic Growth",
      riskScore: 75,
      status: "Active",
      lastUpdated: "2024-01-14",
      performance: {
        ytd: "+5.1%",
        oneYear: "+28.7%",
        threeYear: "+72.5%",
        volatility: "24.8%",
        beta: 1.45,
        alpha: 7.2,
      },
    },
    {
      id: 25,
      name: "NQ Bond",
      type: "Fixed Income",
      riskScore: 25,
      status: "Active",
      lastUpdated: "2024-01-12",
      performance: {
        ytd: "+0.9%",
        oneYear: "+5.6%",
        threeYear: "+18.3%",
        volatility: "5.4%",
        beta: 0.25,
        alpha: 1.8,
      },
    },
  ];

  // Mock data for market indices
  const marketIndices = [
    {
      name: "S&P 500",
      value: "4,783.83",
      change: "-32.57",
      percentChange: "-0.68%",
      color: "red",
    },
    {
      name: "Dow 30",
      value: "37,466.11",
      change: "-118.04",
      percentChange: "-0.31%",
      color: "red",
    },
    {
      name: "Nasdaq",
      value: "14,894.19",
      change: "+12.59",
      percentChange: "+0.08%",
      color: "green",
    },
    {
      name: "Russell 2000",
      value: "1,950.96",
      change: "+15.31",
      percentChange: "+0.79%",
      color: "green",
    },
  ];

  // Add mock data for all holdings and sector breakdown
  const getAllHoldings = (modelId) => [
    {
      symbol: "AAPL",
      name: "Apple Inc.",
      weight: "8.5%",
      change: "+2.15%",
      sector: "Technology",
    },
    {
      symbol: "MSFT",
      name: "Microsoft Corp.",
      weight: "7.2%",
      change: "-0.87%",
      sector: "Technology",
    },
    {
      symbol: "NVDA",
      name: "NVIDIA Corp.",
      weight: "6.8%",
      change: "+1.54%",
      sector: "Technology",
    },
    {
      symbol: "GOOGL",
      name: "Alphabet Inc.",
      weight: "5.9%",
      change: "+0.92%",
      sector: "Technology",
    },
    {
      symbol: "AMZN",
      name: "Amazon.com Inc.",
      weight: "5.5%",
      change: "+1.23%",
      sector: "Consumer Discretionary",
    },
    {
      symbol: "META",
      name: "Meta Platforms Inc.",
      weight: "4.8%",
      change: "+0.76%",
      sector: "Technology",
    },
    {
      symbol: "BRK.B",
      name: "Berkshire Hathaway",
      weight: "4.2%",
      change: "-0.45%",
      sector: "Financials",
    },
    {
      symbol: "JPM",
      name: "JPMorgan Chase",
      weight: "3.8%",
      change: "-0.62%",
      sector: "Financials",
    },
    {
      symbol: "JNJ",
      name: "Johnson & Johnson",
      weight: "3.5%",
      change: "+0.34%",
      sector: "Healthcare",
    },
    {
      symbol: "V",
      name: "Visa Inc.",
      weight: "3.2%",
      change: "+0.88%",
      sector: "Financials",
    },
  ];

  const getSectorBreakdown = (modelId) => {
    const leftColumn = [
      { sector: "Technology", weight: 33 },
      { sector: "Healthcare", weight: 12 },
      { sector: "Financials", weight: 15 },
      { sector: "Consumer Discretionary", weight: 10 },
      { sector: "Communication Services", weight: 7 },
    ];
    const rightColumn = [
      { sector: "Industrials", weight: 8 },
      { sector: "Consumer Staples", weight: 5 },
      { sector: "Real Estate", weight: 3 },
      { sector: "Energy", weight: 4 },
      { sector: "Materials", weight: 2 },
      { sector: "Utilities", weight: 1 },
    ];
    return { leftColumn, rightColumn };
  };

  // Enhanced performance data with more data points
  const getPerformanceData = (timeframe, model) => {
    const now = new Date();
    const maxDays = 1095; // 3 years

    // Generate or retrieve full dataset
    if (!performanceCache || performanceCache.modelId !== model.id) {
      const dates = [];
      const modelData = [];
      const benchmarkData = [];

      // Generate dates for full 3Y period
      for (let i = maxDays; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        dates.push(date);
      }

      // Use 3Y return for the full simulation
      const targetReturn = parseFloat(model.performance.threeYear.replace(/[+%]/g, ''));
      const benchmarkReturn = targetReturn * 0.65; // Consistent 65% of model return

      // Use 3Y volatility parameters
      const volatilityParams = {
        maxDeviation: parseFloat(model.performance.ytd.replace(/[+%]/g, '')) * 0.075,
        cycleLength: 180,
        dailyNoise: 0.005
      };

      const modelDailyReturn = Math.exp(Math.log(1 + targetReturn/100) / maxDays) - 1;
      const benchmarkDailyReturn = Math.exp(Math.log(1 + benchmarkReturn/100) / maxDays) - 1;

      let modelValue = 100;
      let benchmarkValue = 100;
      
      modelData.push(modelValue);
      benchmarkData.push(benchmarkValue);

      // Generate the full 3Y simulation
      for (let i = 1; i <= maxDays; i++) {
        const cycleFactor = Math.sin(2 * Math.PI * i / volatilityParams.cycleLength);
        const marketVariation = (Math.random() - 0.5) * volatilityParams.dailyNoise;
        const modelVariation = (Math.random() - 0.5) * volatilityParams.dailyNoise * 0.5;
        const benchmarkVariation = (Math.random() - 0.5) * volatilityParams.dailyNoise * 0.5;

        const modelDailyVolatility = 
          modelDailyReturn +
          (cycleFactor * volatilityParams.maxDeviation / 100 / volatilityParams.cycleLength) +
          marketVariation +
          modelVariation;

        const benchmarkDailyVolatility = 
          benchmarkDailyReturn +
          (cycleFactor * volatilityParams.maxDeviation / 100 / volatilityParams.cycleLength) +
          marketVariation +
          benchmarkVariation;

        modelValue *= (1 + modelDailyVolatility);
        benchmarkValue *= (1 + benchmarkDailyVolatility);

        modelData.push(modelValue);
        benchmarkData.push(benchmarkValue);
      }

      // Store the full dataset in cache
      const newCache = {
        modelId: model.id,
        dates,
        modelData,
        benchmarkData
      };
      setPerformanceCache(newCache);

      // Return sliced data from the newly generated dataset
      let startIndex = getStartIndex(timeframe, dates, now);
      return {
        labels: dates.slice(startIndex),
        datasets: [
          {
            label: "Model Performance",
            data: normalizeData(modelData.slice(startIndex), timeframe, model),
            borderColor: "#029831",
            backgroundColor: "rgba(2, 152, 49, 0.1)",
            fill: true,
            tension: 0.2,
          },
          {
            label: "Benchmark",
            data: normalizeData(benchmarkData.slice(startIndex), timeframe, model).map(v => v * 0.65),
            borderColor: "#202EC6",
            backgroundColor: "rgba(32, 46, 198, 0.1)",
            fill: true,
            tension: 0.2,
          },
        ],
      };
    }

    // Helper function to get start index based on timeframe
    function getStartIndex(timeframe, dates, now) {
      let startIndex = 0;
      switch (timeframe) {
        case "1M":
          startIndex = dates.length - 31; // Last 30 days
          break;
        case "3M":
          startIndex = dates.length - 91; // Last 90 days
          break;
        case "YTD":
          startIndex = dates.findIndex(date => 
            date.getFullYear() === now.getFullYear() && 
            date.getMonth() === 0 && 
            date.getDate() === 1
          );
          break;
        case "1Y":
          startIndex = dates.length - 366; // Last 365 days
          break;
        case "3Y":
          startIndex = 0; // Full dataset
          break;
        default:
          startIndex = dates.length - 366; // Default to 1Y
      }
      return Math.max(0, startIndex);
    }

    // Helper function to normalize data and adjust to target return
    function normalizeData(data, timeframe, model, isBenchmark = false) {
      const startValue = data[0];
      const normalizedData = data.map(value => (value / startValue) * 100);
      
      // Get target return for the timeframe
      let targetReturn;
      switch(timeframe) {
        case '1M':
          targetReturn = parseFloat(model.performance.ytd.replace(/[+%]/g, '')) / 12;
          break;
        case '3M':
          targetReturn = parseFloat(model.performance.ytd.replace(/[+%]/g, '')) / 4;
          break;
        case 'YTD':
          targetReturn = parseFloat(model.performance.ytd.replace(/[+%]/g, ''));
          break;
        case '1Y':
          targetReturn = parseFloat(model.performance.oneYear.replace(/[+%]/g, ''));
          break;
        case '3Y':
          targetReturn = parseFloat(model.performance.threeYear.replace(/[+%]/g, ''));
          break;
        default:
          targetReturn = parseFloat(model.performance.oneYear.replace(/[+%]/g, ''));
      }

      // For benchmark, adjust target return
      if (isBenchmark) {
        targetReturn *= 0.65;
      }

      // Adjust final value to match target return exactly
      const currentReturn = (normalizedData[normalizedData.length - 1] - 100);
      const adjustmentFactor = (targetReturn) / (currentReturn);
      
      return normalizedData.map(value => {
        const change = value - 100;
        return 100 + (change * adjustmentFactor);
      });
    }

    // Use cached data
    let startIndex = getStartIndex(timeframe, performanceCache.dates, now);
    const slicedModelData = performanceCache.modelData.slice(startIndex);
    const slicedBenchmarkData = performanceCache.benchmarkData.slice(startIndex);

    return {
      labels: performanceCache.dates.slice(startIndex),
      datasets: [
        {
          label: "Model Performance",
          data: normalizeData(slicedModelData, timeframe, model, false),
          borderColor: "#029831",
          backgroundColor: "rgba(2, 152, 49, 0.1)",
          fill: true,
          tension: 0.2,
        },
        {
          label: "Benchmark",
          data: normalizeData(slicedBenchmarkData, timeframe, model, true),
          borderColor: "#202EC6",
          backgroundColor: "rgba(32, 46, 198, 0.1)",
          fill: true,
          tension: 0.2,
        },
      ],
    };
  };

  // Enhanced Market Research Section
  const renderMarketResearch = () => (
    <div className="space-y-6">
      {/* Market Overview */}
      <div className="grid grid-cols-4 gap-4">
        {marketIndices.map((index) => (
          <div key={index.name} className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">{index.name}</div>
            <div className="text-xl font-bold text-gray-700 dark:text-gray-300">{index.value}</div>
            <div
              className={`text-sm ${index.color === "red" ? "text-red-600" : "text-green-600"}`}
            >
              {index.change} ({index.percentChange})
            </div>
          </div>
        ))}
      </div>

      {/* Technical Analysis */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Technical Analysis
          </h3>
          <button
            onClick={() => setShowTechnicalModal(!showTechnicalModal)}
            className="text-[#202EC6] hover:text-opacity-80"
          >
            {showTechnicalModal ? "Hide" : "Show"} Chart
          </button>
        </div>
        {showTechnicalModal && (
            <TechnicalAnalysisChart
              chartOptions={chartOptions}
              technicalIndicators={technicalIndicators}
              selectedIndicators={selectedIndicators}
              setSelectedIndicators={setSelectedIndicators}
              chartTimeframe={chartTimeframe}
              setChartTimeframe={setChartTimeframe}
            />
          )}
      </div>

      {/* Research Tools */}
      <div className="grid grid-cols-2 gap-6">
        {/* Enhanced Screener */}
        <div className="bg-surface-paper dark:bg-surface-paper-dark rounded-lg shadow-elevation-1 dark:shadow-elevation-1-dark p-6">
          <h3 className="text-lg font-semibold mb-4 text-primary dark:text-primary-dark">
            Advanced Screener
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary dark:text-primary-dark mb-1">
                Market Cap
              </label>
              <select className={selectBaseClasses}>
                <option>All</option>
                <option>Mega Cap (&gt;$200B)</option>
                <option>Large Cap ($10B-$200B)</option>
                <option>Mid Cap ($2B-$10B)</option>
                <option>Small Cap ($300M-$2B)</option>
                <option>Micro Cap (&lt;$300M)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary dark:text-primary-dark mb-1">
                Sector
              </label>
              <select className={selectBaseClasses}>
                <option>All Sectors</option>
                <option>Technology</option>
                <option>Healthcare</option>
                <option>Financials</option>
                <option>Consumer Discretionary</option>
                <option>Consumer Staples</option>
                <option>Industrials</option>
                <option>Energy</option>
                <option>Materials</option>
                <option>Utilities</option>
                <option>Real Estate</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary dark:text-primary-dark mb-1">
                Fundamentals
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-secondary dark:text-secondary-dark mb-1">
                    P/E Ratio
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      className={inputBaseClasses}
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      className={inputBaseClasses}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-secondary dark:text-secondary-dark mb-1">
                    Dividend Yield
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      className={inputBaseClasses}
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      className={inputBaseClasses}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary dark:text-primary-dark mb-1">
                Technical Indicators
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-secondary dark:text-secondary-dark mb-1">
                    RSI (14)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      className={inputBaseClasses}
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      className={inputBaseClasses}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-secondary dark:text-secondary-dark mb-1">
                    50-Day MA
                  </label>
                  <select className={selectBaseClasses}>
                    <option>Above 200-Day MA</option>
                    <option>Below 200-Day MA</option>
                    <option>Any</option>
                  </select>
                </div>
              </div>
            </div>
            <button className={primaryButtonClasses}>
              Run Screen
            </button>
          </div>
        </div>

        {/* Enhanced Watchlist */}
        <div className="bg-surface-paper dark:bg-surface-paper-dark rounded-lg shadow-elevation-1 dark:shadow-elevation-1-dark p-6">
          <h3 className="text-lg font-semibold mb-4 text-primary dark:text-primary-dark">
            Enhanced Watchlist
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm font-medium text-primary dark:text-primary-dark">
              <span>Symbol</span>
              <span>Last Price</span>
              <span>Change</span>
              <span>Volume</span>
              <span>RSI</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center text-primary dark:text-primary-dark">
                <span>AAPL</span>
                <span>185.92</span>
                <span className="text-success dark:text-success-dark">+2.15%</span>
                <span>32.5M</span>
                <span>65.4</span>
              </div>
              <div className="flex justify-between items-center text-primary dark:text-primary-dark">
                <span>MSFT</span>
                <span>389.55</span>
                <span className="text-error dark:text-error-dark">-0.87%</span>
                <span>28.1M</span>
                <span>58.2</span>
              </div>
              <div className="flex justify-between items-center text-primary dark:text-primary-dark">
                <span>NVDA</span>
                <span>547.10</span>
                <span className="text-success dark:text-success-dark">+1.23%</span>
                <span>42.3M</span>
                <span>72.1</span>
              </div>
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Add symbol..."
                className={`${inputBaseClasses} bg-surface-paper dark:bg-surface-paper-dark`}
              />
              <button className={secondaryButtonClasses}>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderModelAnalysis = () => (
    <div className="space-y-6">
      {selectedModel ? (
        <>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-primary dark:text-primary-dark">{selectedModel.name}</h2>
              <p className="text-sm text-secondary dark:text-secondary-dark">{selectedModel.type}</p>
            </div>
            <button
              onClick={() => setSelectedModel(null)}
              className="text-primary dark:text-primary-dark hover:text-primary/80 dark:hover:text-primary-dark/80"
            >
              ← Back to Models
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Performance Chart */}
            <div className="bg-surface-paper dark:bg-surface-paper-dark rounded-lg shadow-elevation-1 dark:shadow-elevation-1-dark p-6">
              <h3 className="text-lg font-semibold mb-4 text-primary dark:text-primary-dark">
                Historical Performance
              </h3>
              <Line
                data={getPerformanceData(chartTimeframe, selectedModel)}
                options={chartOptions}
              />
              <div className="flex justify-center space-x-4 mt-4">
                {["1M", "3M", "YTD", "1Y", "3Y"].map((timeframe) => (
                  <button
                    key={timeframe}
                    onClick={() => setChartTimeframe(timeframe)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      chartTimeframe === timeframe
                        ? 'bg-[#029831] text-white shadow-lg scale-105'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                    }`}
                  >
                    {timeframe}
                  </button>
                ))}
              </div>
            </div>

            {/* Key Metrics */}
            <div className="bg-surface-paper dark:bg-surface-paper-dark rounded-lg shadow-elevation-1 dark:shadow-elevation-1-dark p-6">
              <h3 className="text-lg font-semibold mb-4 text-primary dark:text-primary-dark">
                Key Metrics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-secondary dark:text-secondary-dark">Risk Score</span>
                    <div className="text-xl font-medium text-primary dark:text-primary-dark">
                      {selectedModel.riskScore}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-secondary dark:text-secondary-dark">YTD Return</span>
                    <div className={`text-xl font-medium ${
                      selectedModel.performance.ytd.startsWith('+')
                        ? 'text-[#029831]'
                        : 'text-red-500'
                    }`}>
                      {selectedModel.performance.ytd}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-secondary dark:text-secondary-dark">1Y Return</span>
                    <div className={`text-xl font-medium ${
                      selectedModel.performance.oneYear.startsWith('+')
                        ? 'text-[#029831]'
                        : 'text-red-500'
                    }`}>
                      {selectedModel.performance.oneYear}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-secondary dark:text-secondary-dark">3Y Return</span>
                    <div className={`text-xl font-medium ${
                      selectedModel.performance.threeYear.startsWith('+')
                        ? 'text-[#029831]'
                        : 'text-red-500'
                    }`}>
                      {selectedModel.performance.threeYear}
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-secondary dark:text-secondary-dark">Volatility</span>
                    <div className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {selectedModel.performance.volatility}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-secondary dark:text-secondary-dark">Beta</span>
                    <div className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {selectedModel.performance.beta}
                    </div>
                  </div>
                                      <div>
                    <span className="text-sm text-secondary dark:text-secondary-dark">Alpha</span>
                    <div className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {selectedModel.performance.alpha}%
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-secondary dark:text-secondary-dark">Sharpe Ratio</span>
                    <div className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      1.85
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Holdings and Additional Analytics */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Top Holdings
                </h3>
                <button
                  onClick={() => setShowAllHoldings(!showAllHoldings)}
                  className="text-[#202EC6] hover:text-opacity-80"
                >
                  {showAllHoldings ? "Show Less" : "View All Holdings"}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-left text-sm font-medium text-gray-900 dark:text-gray-100">
                      <th className="pb-3">Symbol</th>
                      <th className="pb-3">Name</th>
                      <th className="pb-3">Weight</th>
                      <th className="pb-3">Change</th>
                      {showAllHoldings && <th className="pb-3">Sector</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(showAllHoldings
                      ? getAllHoldings(selectedModel.id)
                      : getAllHoldings(selectedModel.id).slice(0, 3)
                    ).map((holding, index) => (
                      <tr key={holding.symbol} className="text-gray-900 dark:text-gray-100">
                        <td className="py-3">{holding.symbol}</td>
                        <td>{holding.name}</td>
                        <td>{holding.weight}</td>
                        <td
                          className={
                            holding.change.startsWith("+")
                              ? "text-green-600"
                              : "text-red-600"
                          }
                        >
                          {holding.change}
                        </td>
                        {showAllHoldings && <td>{holding.sector}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                Asset Allocation
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-900 dark:text-gray-100">US Equities</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">88%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mt-1">
                    <div className="h-full bg-[#029831] rounded-full" style={{ width: '88%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-900 dark:text-gray-100">International Equities</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">3%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mt-1">
                    <div className="h-full bg-[#F59E0B] rounded-full" style={{ width: '3%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-900 dark:text-gray-100">Fixed Income</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">7%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mt-1">
                    <div className="h-full bg-[#9333EA] rounded-full" style={{ width: '7%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-900 dark:text-gray-100">Cash</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">2%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mt-1">
                    <div className="h-full bg-[#EC4899] rounded-full" style={{ width: '2%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sector Breakdown */}
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 mt-4 text-gray-900 dark:text-gray-100">
              Sector Breakdown
            </h3>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="space-y-1.5">
                {getSectorBreakdown(selectedModel.id).leftColumn.map((sector) => (
                  <div
                    key={sector.sector}
                    className="flex items-center space-x-2"
                  >
                    <div className="flex-1">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{sector.sector}</div>
                      <div className="h-2 bg-gray-200 rounded-full mt-1">
                        <div
                          className="h-full bg-[#202EC6] rounded-full"
                          style={{ width: `${sector.weight}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {sector.weight}%
                    </span>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                {getSectorBreakdown(selectedModel.id).rightColumn.map((sector) => (
                  <div
                    key={sector.sector}
                    className="flex items-center space-x-2"
                  >
                    <div className="flex-1">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{sector.sector}</div>
                      <div className="h-2 bg-gray-200 rounded-full mt-1">
                        <div
                          className="h-full bg-[#202EC6] rounded-full"
                          style={{ width: `${sector.weight}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {sector.weight}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="max-w-xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Select a Model to Analyze</h3>
            <div className="space-y-4">
              <select
                className="w-full p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                onChange={(e) => {
                  const model = models.find(m => m.id === parseInt(e.target.value));
                  setSelectedModel(model);
                }}
                value=""
              >
                <option value="" disabled>Choose a model...</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {model.type} (Risk Score: {model.riskScore})
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select a model from the dropdown above or visit the Model Library tab to view and select models.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Model Library Section
  const renderModelLibrary = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Model Library</h2>
        <button
          onClick={() => setShowModelCreator(true)}
          className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]"
        >
          Create New Model
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {models.map((model) => (
          <div
            key={model.id}
            className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => {
              setSelectedModel(model);
              setShowModelModal(true);
            }}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{model.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{model.type}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs ${
                model.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100'
              }`}>
                {model.status}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Risk Score:</span>
                <span className="font-medium text-gray-600 dark:text-gray-300">{model.riskScore}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">YTD Return:</span>
                <span className="font-medium text-green-600">{model.performance.ytd}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">1Y Return:</span>
                <span className="font-medium text-green-600">{model.performance.oneYear}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Enhanced Model Creation Form
  const renderModelCreator = () => (
    <Modal
      isOpen={showModelCreator}
      onClose={() => setShowModelCreator(false)}
      title="Create New Model"
      size="xl"
    >
      <div className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Model Name
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                placeholder="Enter model name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Model Type
              </label>
              <select className="w-full p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                <option>Equity</option>
                <option>Fixed Income</option>
                <option>Balanced</option>
                <option>Alternative</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Risk Score (1-100)
              </label>
              <input
                type="number"
                min="1"
                max="100"
                className="w-full p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Target Return
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  step="0.1"
                  className="w-full p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                  placeholder="Enter target return"
                />
                <span className="text-gray-700 dark:text-gray-300">%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Benchmark
              </label>
              <select className="w-full p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                <option>S&P 500</option>
                <option>NASDAQ Composite</option>
                <option>Dow Jones Industrial Average</option>
                <option>Custom...</option>
              </select>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Investment Strategy
              </label>
              <select className="w-full p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                <option>Growth</option>
                <option>Value</option>
                <option>Income</option>
                <option>Momentum</option>
                <option>Blend</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rebalancing Frequency
              </label>
              <select className="w-full p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                <option>Monthly</option>
                <option>Quarterly</option>
                <option>Semi-Annually</option>
                <option>Annually</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Asset Allocation
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    className="w-20 p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                    placeholder="0"
                  />
                  <span className="text-gray-700 dark:text-gray-300">% Equities</span>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    className="w-20 p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                    placeholder="0"
                  />
                  <span className="text-gray-700 dark:text-gray-300">% Fixed Income</span>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    className="w-20 p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                    placeholder="0"
                  />
                  <span className="text-gray-700 dark:text-gray-300">% Cash</span>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    className="w-20 p-2 border rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                    placeholder="0"
                  />
                  <span className="text-gray-700 dark:text-gray-300">% Alternatives</span>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Investment Constraints
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" />
                  <span>ESG Focused</span>
                </label>
                <label className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" />
                  <span>Tax Efficient</span>
                </label>
                <label className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" />
                  <span>Sector Restrictions</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={() => setShowModelCreator(false)}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Cancel
          </button>
          <button className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]">
            Create Model
          </button>
        </div>
      </div>
    </Modal>
  );

  const breadcrumbItems = [
    { label: "Portfolio Management", link: "/portfolio-management" },
    { label: "Market Research", link: "/portfolio-management/market-research" },
  ];

  return (
    <SubPageLayout
      title="Market Research"
      subtitle="Analyze market trends and investment models"
      breadcrumbItems={breadcrumbItems}
    >
      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'models', label: 'Model Library' },
            { id: 'research', label: 'Market Research' },
            { id: 'analysis', label: 'Model Analysis' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 text-sm font-medium border-b-2 ${
                activeTab === tab.id
                  ? 'border-[#029831] text-[#029831]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'models' && renderModelLibrary()}
      {activeTab === 'research' && renderMarketResearch()}
      {activeTab === 'analysis' && renderModelAnalysis()}

      {/* Model Details Modal */}
      <Modal
        isOpen={showModelModal}
        onClose={() => setShowModelModal(false)}
        title={selectedModel ? `${selectedModel.name} Details` : "Model Details"}
        size="xl"
      >
        <div className="p-6">
          {selectedModel && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Performance Metrics
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">YTD</p>
                      <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {selectedModel.performance.ytd}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">1 Year</p>
                      <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {selectedModel.performance.oneYear}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">3 Year</p>
                      <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {selectedModel.performance.threeYear}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Volatility</p>
                      <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {selectedModel.performance.volatility}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Risk Metrics
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Beta</p>
                      <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {selectedModel.performance.beta}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Alpha</p>
                      <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {selectedModel.performance.alpha}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => {
                    setShowHoldingsModal(true);
                    setShowModelModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#202EC6] border border-transparent rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  View Holdings
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Technical Analysis Modal */}
      <Modal
        isOpen={showTechnicalModal}
        onClose={() => setShowTechnicalModal(false)}
        title="Technical Analysis"
        size="xl"
      >
        <div className="p-6 bg-surface-paper dark:bg-surface-paper-dark">
          <TechnicalAnalysisChart
            chartOptions={chartOptions}
            technicalIndicators={technicalIndicators}
            selectedIndicators={selectedIndicators}
            setSelectedIndicators={setSelectedIndicators}
            chartTimeframe={chartTimeframe}
            setChartTimeframe={setChartTimeframe}
          />
        </div>
      </Modal>

      {/* Holdings Modal */}
      <Modal
        isOpen={showHoldingsModal}
        onClose={() => setShowHoldingsModal(false)}
        title={selectedModel ? `${selectedModel.name} Holdings` : "Holdings"}
        size="xl"
      >
        <div className="p-6">
          {selectedModel && (
            <div className="space-y-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Symbol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Weight
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Sector
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Change
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {getAllHoldings(selectedModel.id).map((holding, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                          {holding.symbol}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {holding.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {holding.weight}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {holding.sector}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                          holding.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {holding.change}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Model Creator Modal */}
      {showModelCreator && renderModelCreator()}
    </SubPageLayout>
  );
};

export default MarketResearch;
